import { FC, RefObject, useMemo, useRef } from "react";
import {
  CoreFieldValue,
  FieldFormV2,
  FieldFormV2Module,
  FieldV2,
  LoadingV2,
  NumericFieldProps,
  RelativityTooltipV2,
  SingleCheckboxFieldProps,
  useFieldSchemaV2FactoryContext,
} from "@ucl/library";
import { FIELD_KEYS } from "@/constants/fieldKeys";
import { PersonInformationComplexValue } from "../ComplexFields/PersonInformationComplexField";
import { KeyPerson } from "@/types/KeyPerson";
import { FinancialApplicationConditionalFlags } from "@/hooks/useFinancialApplicationCoreData";
import { createKeyPersonFormBuilder } from "./KeyPersonFormBuilder";
import { FinancialApplicationFormSectionContextType } from "@/hooks/useFinancialApplicationFormSectionValidation";
import { DigitalBankingInformationComplexFieldValue } from "../ComplexFields/DigitalBankingInformationComplexField";
import { EmploymentInformationComplexValue } from "../ComplexFields/EmploymentInformationComplexField";
import { GovernmentIdInformationComplexValue } from "../ComplexFields/GovernmentIdInformationComplexField";
import { SecurityQuestionsInformationComplexValue } from "../ComplexFields/SecurityQuestionsInformationComplexField";
import { DebitCardInformationComplexValue } from "../ComplexFields/DebitCardInformationComplexField";

interface KeyPersonFormProps {
  fieldFormModule: RefObject<FieldFormV2Module<KeyPerson> | null>;
  formValue: KeyPerson;
  setFormValue: (newValue: KeyPerson, newFieldValue: CoreFieldValue, fieldProps: any) => void;
  saveFormData: (value: KeyPerson) => Promise<void>;
  debitCardInformationRenderKey: string;
  digitalBankingInformationRenderKey: string;
  conditionalFlags: FinancialApplicationConditionalFlags;
  validationErrors: Record<string, string[]>;
  onBlur?: (field: CoreFieldValue) => void;
  readOnly?: boolean;
  alreadyHasControlPersonRole: boolean;
  validationContextType: FinancialApplicationFormSectionContextType;
  availableFields?: string[];
  shouldHideSecurityQuestions?: boolean;
  shouldAskIfCanProvideGovernmentIdInformation?: boolean;
  shouldAlwaysShowGovernmentIdInformationFields?: boolean;
  shouldShowDigitalBankingFields?: boolean;
  shouldShowDebitCardFields?: boolean;
}

export const KeyPersonForm: FC<KeyPersonFormProps> = ({
  fieldFormModule,
  formValue,
  setFormValue,
  saveFormData,
  debitCardInformationRenderKey,
  digitalBankingInformationRenderKey,
  conditionalFlags,
  validationErrors,
  onBlur,
  readOnly,
  alreadyHasControlPersonRole,
  validationContextType,
  availableFields,
  shouldHideSecurityQuestions,
  shouldAskIfCanProvideGovernmentIdInformation,
  shouldAlwaysShowGovernmentIdInformationFields,
  shouldShowDigitalBankingFields,
  shouldShowDebitCardFields,
}) => {
  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  const applicableRolesFieldsReadyLoadedRef = useRef(false);

  const formBuilder = useMemo(() => {
    if (!schemaFactory) return undefined;
    return createKeyPersonFormBuilder(schemaFactory, validationContextType);
  }, [schemaFactory]);

  if (!schemaFactory || !formBuilder || !formValue) {
    return (
      <section className="key-person-form">
        <LoadingV2 />
      </section>
    );
  }

  const isApplicableRolesRequired =
    !formValue.isBeneficialOwner && !formValue.isAuthorizedSigner && !formValue.isControlPerson;

  const isApplicableRolesAvailable =
    !availableFields ||
    availableFields.includes(FIELD_KEYS.KEY_PERSON.IS_BENEFICIAL_OWNER) ||
    availableFields.includes(FIELD_KEYS.KEY_PERSON.IS_AUTHORIZED_SIGNER) ||
    availableFields.includes(FIELD_KEYS.KEY_PERSON.IS_CONTROL_PERSON);

  const isControlPersonDisabled = alreadyHasControlPersonRole && !formValue.isControlPerson;

  const applicableRolesFieldsReadyHandle = () => {
    if (!applicableRolesFieldsReadyLoadedRef.current) {
      applicableRolesFieldsReadyLoadedRef.current = true;
    }
  };

  return (
    <div className="key-person-form">
      <FieldFormV2<KeyPerson>
        ref={fieldFormModule}
        value={formValue}
        fieldSchemaFormBuilder={formBuilder}
        onFormSubmit={async (value) => {
          await saveFormData(value);
        }}
        onFieldChanged={(newValue, newFieldValue, fieldProps) => {
          setFormValue(newValue, newFieldValue, fieldProps);
        }}
        errors={validationErrors}
        availableFields={availableFields}
      >
        {isApplicableRolesAvailable && (
          // Workaround to ensure the applicable roles fields are ready before displaying them
          <div className="key-person-applicable-roles">
            <h4>Applicable Roles</h4>
            <FieldV2
              fieldProps={
                {
                  type: "SingleCheckbox",
                  disabled: readOnly,
                  fieldKey: FIELD_KEYS.KEY_PERSON.IS_BENEFICIAL_OWNER,
                  label: "",
                  isRequired: isApplicableRolesRequired,
                } as SingleCheckboxFieldProps
              }
              handleOnBlur={onBlur}
              onReady={() => applicableRolesFieldsReadyHandle()}
            />
            <FieldV2
              fieldProps={
                {
                  type: "SingleCheckbox",
                  disabled: readOnly,
                  fieldKey: FIELD_KEYS.KEY_PERSON.IS_AUTHORIZED_SIGNER,
                  label: "",
                  isRequired: isApplicableRolesRequired,
                } as SingleCheckboxFieldProps
              }
              handleOnBlur={onBlur}
              onReady={applicableRolesFieldsReadyHandle}
            />
            <RelativityTooltipV2
              shouldShowTooltip={alreadyHasControlPersonRole && !formValue.isControlPerson}
              content="Only one individual can be the Control Person."
            >
              <FieldV2
                fieldProps={
                  {
                    type: "SingleCheckbox",
                    disabled: readOnly || isControlPersonDisabled,
                    fieldKey: FIELD_KEYS.KEY_PERSON.IS_CONTROL_PERSON,
                    label: "",
                    isRequired: isApplicableRolesRequired && !isControlPersonDisabled,
                  } as SingleCheckboxFieldProps
                }
                handleOnBlur={onBlur}
                onReady={applicableRolesFieldsReadyHandle}
              />
            </RelativityTooltipV2>
          </div>
        )}
        <div className="key-person-additionalinformation">
          <div className="key-person-additionalinformation-fields">
            <FieldV2
              fieldProps={
                {
                  fieldKey: FIELD_KEYS.KEY_PERSON.BENEFICIAL_OWNER_PERCENTAGE,
                  hidden: !formValue?.isBeneficialOwner,
                  readOnly: readOnly,
                  showIncrementButtons: false,
                  placeholder: "1.00%",
                } as NumericFieldProps
              }
              handleOnBlur={onBlur}
            />
          </div>
        </div>
        <FieldV2<PersonInformationComplexValue>
          fieldProps={FIELD_KEYS.KEY_PERSON.PERSON_INFORMATION}
          complexFieldValue={{
            readOnly: formValue.isApplicant || readOnly,
            showHeader: true,
            headerLabel: "Personal Information",
            hiddenFields: ["PHONE_NUMBER", "EMAIL_ADDRESS", "HOME_ADDRESS"],
            className: "personal-information",
          }}
          handleOnBlur={onBlur}
        />
        <FieldV2<GovernmentIdInformationComplexValue>
          fieldProps={FIELD_KEYS.KEY_PERSON.GOVERNMENT_ID_INFORMATION}
          complexFieldValue={{
            readOnly: formValue.isApplicant || readOnly,
            showHeader: true,
            headerLabel: "Identification Information",
            shouldAskIfCanProvideGovernmentIdInformation: shouldAskIfCanProvideGovernmentIdInformation,
            canProvideGovernmentIdInformation: formValue.governmentIdInformation?.canProvideGovernmentIdInformation,
            shouldAlwaysShowGovernmentIdInformationFields: shouldAlwaysShowGovernmentIdInformationFields,
          }}
          handleOnBlur={onBlur}
        />
        <FieldV2<PersonInformationComplexValue>
          fieldProps={FIELD_KEYS.KEY_PERSON.PERSON_INFORMATION}
          complexFieldValue={{
            readOnly: formValue.isApplicant || readOnly,
            showHeader: true,
            headerLabel: "Contact Information",
            hiddenFields: [
              "FIRST_NAME",
              "MIDDLE_NAME",
              "LAST_NAME",
              "DATE_OF_BIRTH",
              "SOCIAL_SECURITY_NUMBER",
            ],
            className: "contact-information",
          }}
        />
        <FieldV2<EmploymentInformationComplexValue>
          fieldProps={FIELD_KEYS.KEY_PERSON.EMPLOYMENT_INFORMATION}
          complexFieldValue={{
            readOnly: formValue.isApplicant || readOnly,
            showAttorneyBarNumber: conditionalFlags.shouldShowAttorneyBarNumber,
            showHeader: true,
            headerLabel: "Employment Information",
          }}
          handleOnBlur={onBlur}
        />
        {!formValue.isApplicant && (
          <>
            <FieldV2<DigitalBankingInformationComplexFieldValue>
              key={digitalBankingInformationRenderKey}
              fieldProps={FIELD_KEYS.KEY_PERSON.DIGITAL_BANKING_INFORMATION}
              complexFieldValue={{
                readOnly: formValue.isApplicant || readOnly,
                showHeader: true,
                headerLabel: "Digital Banking",
                shouldShowDigitalBankingFields: shouldShowDigitalBankingFields,
              }}
              handleOnBlur={onBlur}
            />
            <FieldV2<SecurityQuestionsInformationComplexValue>
              fieldProps={FIELD_KEYS.KEY_PERSON.SECURITY_QUESTIONS_INFORMATION}
              complexFieldValue={{
                readOnly: formValue.isApplicant || readOnly,
                showHeader: false,
                shouldHide: shouldHideSecurityQuestions,
              }}
              handleOnBlur={onBlur}
            />
            {formValue.isAuthorizedSigner && (
              <FieldV2<DebitCardInformationComplexValue>
                key={debitCardInformationRenderKey}
                fieldProps={FIELD_KEYS.KEY_PERSON.DEBIT_CARD_INFORMATION}
                complexFieldValue={{
                  readOnly: formValue.isApplicant || readOnly,
                  shouldShowBusinessName: true,
                  showHeader: true,
                  headerLabel: "Debit Card",
                  shouldShowDebitCardFields: shouldShowDebitCardFields,
                }}
                handleOnBlur={onBlur}
              />
            )}
          </>
        )}
      </FieldFormV2>
    </div>
  );
};
